import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { queryClient } from './lib/queryClient';
import { ThemeProvider } from './contexts/ThemeContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { DialogProvider } from './contexts/DialogContext';
import ProtectedRoute from './components/ProtectedRoute';
import RoleGuard from './components/RoleGuard';
import AppShell from './components/layout/AppShell';
import LoadingSpinner from './components/common/LoadingSpinner';
import { quickLoginOffered } from './models/dev-quick-login.model';

// Lazy-loaded pages — each generates an independent bundle
const Login = lazy(() => import('./components/backoffice/Login'));
const DevQuickLogin = lazy(() => import('./components/backoffice/DevQuickLogin'));
const Dashboard = lazy(() => import('./components/backoffice/HomeDashboard'));
const SchoolsPage = lazy(() => import('./components/backoffice/SchoolsPage'));
const TeachersPage = lazy(() => import('./components/backoffice/TeachersPage'));
const AdminActivity = lazy(() => import('./components/backoffice/AdminActivity'));
const SchoolImporter = lazy(() => import('./components/backoffice/SchoolImporter'));
const ManualSchoolForm = lazy(() => import('./components/backoffice/ManualSchoolForm'));
const SchoolDetail = lazy(() => import('./components/backoffice/SchoolDetail'));
const SchoolDuplicatesPage = lazy(() => import('./components/backoffice/school-duplicates/SchoolDuplicatesPage'));
const SchoolControlPage = lazy(() => import('./components/backoffice/school-control/SchoolControlPage'));
const EvaluationRequestsPage = lazy(() => import('./components/backoffice/EvaluationRequestsPage'));
const EvaluationRequestDetail = lazy(() => import('./components/backoffice/EvaluationRequestDetail'));
const EmailLayout = lazy(() => import('./components/backoffice/email/EmailLayout'));
const EmailComposer = lazy(() => import('./components/backoffice/EmailComposer'));
const EmailTemplateManager = lazy(() => import('./components/backoffice/EmailTemplateManager'));
const EmailTemplateDetail = lazy(() => import('./components/backoffice/EmailTemplateDetail'));
const EmailWorkflow = lazy(() => import('./components/backoffice/EmailWorkflow'));
const CrmActions = lazy(() => import('./components/backoffice/CrmActions'));
const EmailHistory = lazy(() => import('./components/backoffice/EmailHistory'));
const EmailSendsPage = lazy(() => import('./components/backoffice/email-sends/EmailSendsPage'));
const EmailSendDetailPage = lazy(() => import('./components/backoffice/email-sends/EmailSendDetailPage'));
const EmailAnalytics = lazy(() => import('./components/backoffice/EmailAnalytics'));
const CampaignListPage = lazy(() => import('./components/backoffice/campaigns/CampaignListPage'));
const SegmentListPage = lazy(() => import('./components/backoffice/campaigns/SegmentListPage'));
const HelpIndexPage = lazy(() => import('./components/backoffice/help/HelpIndexPage'));
const HelpTopicPage = lazy(() => import('./components/backoffice/help/HelpTopicPage'));
const LegacyGuideRedirect = lazy(() => import('./components/backoffice/help/LegacyGuideRedirect'));
const CampaignDraftPage = lazy(() => import('./components/backoffice/campaign-draft/CampaignDraftPage'));
const CampaignControlRoom = lazy(() => import('./components/backoffice/campaign-room/CampaignControlRoom'));
const Products = lazy(() => import('./components/backoffice/Products'));
const SupervisorTeam = lazy(() => import('./components/backoffice/SupervisorTeam'));
const AdminUsers = lazy(() => import('./components/backoffice/AdminUsers'));
const TestEmailsPage = lazy(() => import('./components/backoffice/TestEmailsPage'));
const WarmupSchedulePage = lazy(() => import('./components/backoffice/warmup/WarmupSchedulePage'));
const BouncedEmailsPage = lazy(() => import('./components/backoffice/BouncedEmailsPage'));
const WebinarContactsPage = lazy(() => import('./components/backoffice/WebinarContactsPage'));
const SettingsPage = lazy(() => import('./components/backoffice/SettingsPage'));
const AcceptInvitation = lazy(() => import('./components/backoffice/AcceptInvitation'));
const ForceChangePassword = lazy(() => import('./components/backoffice/ForceChangePassword'));
const ForgotPassword = lazy(() => import('./components/backoffice/ForgotPassword'));
const ResetPassword = lazy(() => import('./components/backoffice/ResetPassword'));

const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
    <ThemeProvider>
    <NotificationProvider>
      <DialogProvider>
        <Router>
          <Suspense fallback={<LoadingSpinner message="Cargando..." />}>
            <Routes>
              {/* Public routes */}
              <Route path="/login" element={<Login />} />
              {quickLoginOffered(import.meta.env.VITE_ENABLE_QUICK_LOGIN) && <Route path="/dev/quick-login" element={<DevQuickLogin />} />}
              <Route path="/invitation/accept" element={<AcceptInvitation />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/password-reset" element={<ResetPassword />} />

              {/* Protected but outside AppShell */}
              <Route path="/force-change-password" element={<ProtectedRoute><ForceChangePassword /></ProtectedRoute>} />

              {/* Protected routes inside AppShell */}
              <Route element={<ProtectedRoute><AppShell /></ProtectedRoute>}>
                <Route index element={<Navigate to="/home" replace />} />
                <Route path="/home" element={<Dashboard />} />
                <Route path="/dashboard" element={<Navigate to="/home" replace />} />
                {/*
                  Telemarket no tiene acceso a la base completa de instituciones —
                  solo a su cartera ("Mis Instituciones"). Si entran por URL directa o
                  por un CTA viejo, los redirigimos a /my-schools (no a /home).
                */}
                <Route
                  path="/schools"
                  element={
                    <RoleGuard
                      allow={['root', 'admin', 'supervisor', 'backseller', 'viewer']}
                      redirectTo="/my-schools"
                    >
                      <SchoolsPage />
                    </RoleGuard>
                  }
                />
                <Route path="/schools/import" element={<SchoolImporter />} />
                {/* Crear colegio: misma allow-list que `POST /admin/schools/create`
                    en el backend. Telemarket/viewer/supervisor → /my-schools. */}
                <Route
                  path="/schools/new"
                  element={
                    <RoleGuard allow={['root', 'admin', 'backseller']} redirectTo="/my-schools">
                      <ManualSchoolForm />
                    </RoleGuard>
                  }
                />
                {/* The administrators' list with lifecycle stages and control queues. Sellers keep /my-schools untouched. */}
                <Route
                  path="/schools/control"
                  element={
                    <RoleGuard allow={['root', 'admin']} redirectTo="/home">
                      <SchoolControlPage />
                    </RoleGuard>
                  }
                />
                {/* Duplicates and merge move people between rows: `root` and `admin`, as the API. */}
                <Route
                  path="/schools/duplicates"
                  element={
                    <RoleGuard allow={['root', 'admin']} redirectTo="/schools">
                      <SchoolDuplicatesPage />
                    </RoleGuard>
                  }
                />
                <Route path="/schools/:id" element={<SchoolDetail />} />
                <Route path="/my-schools" element={<SchoolsPage />} />
                <Route path="/teachers" element={<TeachersPage />} />
                <Route path="/evaluations" element={<EvaluationRequestsPage />} />
                <Route path="/evaluation-requests/:id" element={<EvaluationRequestDetail />} />
                <Route path="/emails/guide" element={<LegacyGuideRedirect />} />
                <Route path="/emails" element={<EmailLayout />}>
                  <Route index element={null} />
                  <Route path="compose" element={<EmailComposer />} />
                  <Route path="workflow" element={<EmailWorkflow />} />
                  <Route path="actions" element={<CrmActions />} />
                  <Route path="templates" element={<EmailTemplateManager />} />
                  <Route path="templates/:id" element={<EmailTemplateDetail />} />
                  <Route path="history" element={<EmailHistory />} />
                  <Route path="sends" element={<EmailSendsPage />} />
                  <Route path="sends/:slug" element={<EmailSendDetailPage />} />
                  <Route path="analytics" element={<EmailAnalytics />} />
                  <Route path="campaigns" element={<CampaignListPage />} />
                  <Route path="campaigns/:id/draft" element={<CampaignDraftPage />} />
                  <Route path="campaigns/:id" element={<CampaignControlRoom />} />
                  <Route path="segments" element={<SegmentListPage />} />
                  <Route path="bounced" element={<BouncedEmailsPage />} />
                  <Route path="warmup" element={<WarmupSchedulePage />} />
                  <Route path="tests" element={<TestEmailsPage />} />
                </Route>
                <Route path="/help" element={<HelpIndexPage />} />
                <Route path="/help/:topic" element={<HelpTopicPage />} />
                <Route path="/products" element={<RoleGuard allow={['root', 'admin']}><Products /></RoleGuard>} />
                <Route path="/team" element={<RoleGuard allow={['supervisor']}><SupervisorTeam /></RoleGuard>} />

                {/* Items administrativos al nivel del menú principal (solo admin/root). */}
                <Route path="/activity" element={<RoleGuard allow={['root', 'admin']}><AdminActivity /></RoleGuard>} />
                <Route path="/users" element={<RoleGuard allow={['root', 'admin']}><AdminUsers /></RoleGuard>} />
                <Route path="/test-emails" element={<Navigate to="/emails/tests" replace />} />
                <Route path="/warmup-schedule" element={<Navigate to="/emails/warmup" replace />} />
                <Route path="/bounced-emails" element={<Navigate to="/emails/bounced" replace />} />
                <Route path="/webinar-contacts" element={<RoleGuard allow={['root', 'admin', 'supervisor']}><WebinarContactsPage /></RoleGuard>} />
                <Route path="/settings" element={<RoleGuard allow={['root', 'admin']}><SettingsPage /></RoleGuard>} />

                {/* Compatibilidad con URLs viejas — redirigen a las rutas top-level. */}
                <Route path="/admin" element={<Navigate to="/activity" replace />} />
                <Route path="/admin/activity" element={<Navigate to="/activity" replace />} />
                <Route path="/admin/users" element={<Navigate to="/users" replace />} />
                <Route path="/admin/test-emails" element={<Navigate to="/emails/tests" replace />} />
                <Route path="/admin-activity" element={<Navigate to="/activity" replace />} />
              </Route>

              {/* Fallback */}
              <Route path="*" element={<Navigate to="/login" replace />} />
            </Routes>
          </Suspense>
        </Router>
      </DialogProvider>
    </NotificationProvider>
    </ThemeProvider>
    {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" />}
    </QueryClientProvider>
  );
};

export default App;
